import { useEffect, useMemo, useRef, useState } from 'react'

import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

import {
  ActionSelections,
  ChatMode,
  EphemeralChatSurface,
  FlowAction,
  FlowMetadata,
} from '../../core/types/chat.types'
import { AgentActivityTrail } from '../../core/types/agentStatus.types'
import { ExploreCatalogCard, ExploreCatalogCategory } from '../../core/types/explore.types'
import { getAcceptString } from '../../core/utils/attachment'
import {
  MaxChatMessage,
  MaxConnectionState,
  MaxSessionStatus,
} from '../../types'
import {
  MaxDownloadReportResponse,
  MaxReportListItem,
} from '../../services/max-report.service'
import { emptyChatComponents, defaultEmptyComponent } from '../states/empty-chat-registry'
import ExploreEmptyState from '../states/explore-empty-state'
import ChatHeader from '../chat/chat-header'
import ChatComposer from '../chat/chat-composer'
import AttachmentDropzone from '../chat/attachment-dropzone'
import MessagesContainer from '../chat/messages-container'
import StatusErrorStrip from '../chat/status-error-strip'
import ReportsListModal from '../reports/reports-list-modal'
import SparkleIcon from '../ui/sparkle-icon'

type ChatAreaProps = {
  headerTitle: string
  currentConversationId: string | null
  currentMessages: MaxChatMessage[]
  connectionState: MaxConnectionState
  isTyping: boolean
  activeAgentTrail?: AgentActivityTrail | null
  agentTrailForMessage?: (message: MaxChatMessage) => AgentActivityTrail | null
  isLoadingMessages: boolean
  sessionStatus: MaxSessionStatus
  sessionError: string | null
  transportError: string | null
  isExploreModeEnabled: boolean
  activeMode: ChatMode
  activeFlowMetadata: FlowMetadata | null
  isAwaitingUploadStep: boolean
  ephemeralSurface?: EphemeralChatSurface
  textDisabled?: boolean
  attachmentsDisabled?: boolean
  isMessageFavorited?: (message: MaxChatMessage) => boolean
  onSend: (content: string) => Promise<boolean>
  onStartCompare: () => Promise<boolean>
  onStartExplore: () => Promise<boolean>
  onStartBudget: () => Promise<boolean>
  onModeSelect: (mode: ChatMode) => void
  onSelectFlowAction: (payload: {
    action: FlowAction
    message: MaxChatMessage
    selections?: ActionSelections
  }) => void
  onToolkitFavorite: (payload: { message: MaxChatMessage }) => void
  onToolkitShare: (payload: { message: MaxChatMessage }) => void
  onToolkitRetry: (payload: { message: MaxChatMessage }) => void
  onToolkitExportBudget: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onToolkitExportReport?: (payload: { message: MaxChatMessage; action: FlowAction }) => void
  onSubmitEphemeralSurface?: (surface: EphemeralChatSurface) => void
  onAttachFiles: (files: FileList | File[]) => void
  // Explore catalog
  exploreIsLoading?: boolean
  exploreError?: string | null
  exploreCategories?: ExploreCatalogCategory[]
  exploreCards?: ExploreCatalogCard[]
  exploreFilteredCards?: ExploreCatalogCard[]
  exploreSelectedCategory?: string
  onExploreSelectCategory?: (categoryId: string) => void
  onExploreSelectCard?: (card: ExploreCatalogCard) => void
  onExploreReload?: () => void
  onExploreApplyCard?: (card: ExploreCatalogCard, selections?: Partial<ActionSelections>) => void
  exploreInitialSelections?: Partial<ActionSelections>
  reports: MaxReportListItem[]
  reportsHasCached: boolean
  reportsLoading: boolean
  reportsError: string | null
  onHeaderOptionSelect: (id: string) => void
  onFetchReports: (conversationId: string) => Promise<MaxReportListItem[]>
  onDownloadReport: (reportId: string, tenantId?: string) => Promise<MaxDownloadReportResponse>
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    background: 'var(--ds-bg-surface, #fff)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  // Top tab bar
  tabBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 56,
    flexShrink: 0,
    backgroundColor: 'var(--ds-bg-surface, #fff)',
    borderBottom: '1px solid var(--ds-border-default, #d7dae4)',
    padding: '0 24px',
  },
  tabGroup: {
    display: 'flex',
    gap: 2,
    padding: 4,
    borderRadius: 10,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
  },
  tab: {
    padding: '6px 16px',
    borderRadius: 7,
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 600,
    fontSize: '0.875rem',
    transition: 'all 200ms ease',
    border: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    color: 'var(--ds-fg-secondary, #535b6c)',
    background: 'transparent',
    '&:hover': {
      color: 'var(--ds-fg-primary, #1f2430)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  activeDefaultTab: {
    background: 'linear-gradient(to right, #737986, #9aa1b4)',
    color: '#fff',
    boxShadow: '0 0 20px rgba(115,121,134,0.5)',
  },
  activeCompareTab: {
    background: 'oklch(79.011% 0.04559 259)',
    color: 'oklch(14.781% 0.0225 237.71)',
    boxShadow: '0 0 20px color-mix(in oklch, oklch(79.011% 0.04559 259) 50%, transparent)',
  },
  activeExploreTab: {
    background: 'linear-gradient(to right, #2f3f69, #506799)',
    color: '#fff',
    boxShadow: '0 0 20px rgba(47,63,105,0.5)',
  },
  activeBudgetTab: {
    background: 'linear-gradient(to right, #f47d6b, #f68778)',
    color: '#fff',
    boxShadow: '0 0 20px rgba(244,125,107,0.5)',
  },
  sparkles: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translateY(-2px)',
  },
  sparkleLarge: { width: 12, height: 12 },
  sparkleSmall: { width: 8, height: 8, transform: 'translateY(-7px) translateX(-1px)' },
  // Main transcript area
  transcriptHost: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  dragOverlay: {
    pointerEvents: 'none',
    position: 'absolute',
    inset: 0,
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dotted rgba(232,115,90,0.6)',
    background: 'rgba(232,115,90,0.2)',
    backdropFilter: 'blur(10px)',
  },
  dragOverlayInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
    padding: '0 24px',
    textAlign: 'center',
  },
  dragIconHost: {
    width: 144,
    height: 144,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 34,
    border: '1px solid rgba(255,255,255,0.85)',
    background: 'linear-gradient(135deg, #fff, rgba(232,115,90,0.1), #fff)',
    boxShadow: '0 22px 60px rgba(233,118,86,0.24)',
    fontSize: '6rem',
  },
  dragLabel: {
    borderRadius: 9999,
    border: '1px solid rgba(255,255,255,0.7)',
    background: 'rgba(255,255,255,0.88)',
    padding: '12px 24px',
    boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
    fontSize: '1.75rem',
    fontWeight: 600,
    letterSpacing: '-0.03em',
    color: 'var(--color-coral-sender, #E8735A)',
  },
  // Session state
  statusState: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  statusCard: {
    maxWidth: 560,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
  },
  loadingSpinner: {
    color: '#2f3f69',
    marginBottom: theme.spacing(1),
  },
  statusTitle: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '1.4rem',
    lineHeight: 1.2,
    fontWeight: 700,
    color: '#1f2430',
  },
  statusBody: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.95rem',
    lineHeight: 1.6,
    color: 'var(--ds-fg-secondary, #647089)',
  },
  // Footer
  footer: {
    flexShrink: 0,
    padding: '12px 24px 18px',
    backgroundColor: 'var(--ds-bg-surface, #fff)',
    borderTop: '1px solid var(--ds-border-default, #d7dae4)',
  },
}))

const ChatArea = ({
  headerTitle,
  currentConversationId,
  currentMessages,
  connectionState,
  isTyping,
  activeAgentTrail,
  agentTrailForMessage,
  isLoadingMessages,
  sessionStatus,
  sessionError,
  transportError,
  isExploreModeEnabled,
  activeMode,
  activeFlowMetadata,
  isAwaitingUploadStep,
  ephemeralSurface,
  textDisabled = false,
  attachmentsDisabled = false,
  isMessageFavorited,
  onSend,
  onStartCompare,
  onStartExplore: _onStartExplore,
  onStartBudget,
  onModeSelect,
  onSelectFlowAction,
  onToolkitFavorite,
  onToolkitShare,
  onToolkitRetry,
  onToolkitExportBudget,
  onToolkitExportReport,
  onSubmitEphemeralSurface,
  onAttachFiles,
  exploreIsLoading,
  exploreError,
  exploreCategories,
  exploreCards,
  exploreFilteredCards,
  exploreSelectedCategory,
  onExploreSelectCategory,
  onExploreSelectCard,
  onExploreReload,
  onExploreApplyCard,
  exploreInitialSelections,
  reports,
  reportsHasCached,
  reportsLoading,
  reportsError,
  onHeaderOptionSelect,
  onFetchReports,
  onDownloadReport,
}: ChatAreaProps) => {
  const classes = useStyles()
  const [showReportsModal, setShowReportsModal] = useState(false)

  const hasMessages = currentMessages.length > 0
  const isCompareMode = activeMode === 'COMPARE'
  const isExploreMode = activeMode === 'EXPLORE'
  const isBudgetMode = activeMode === 'BUDGET'

  const isExploreFlowActive = useMemo(() => {
    if (!isExploreMode) return false
    const flow = activeFlowMetadata?.flow
    return typeof flow === 'string' && flow.toUpperCase() === 'EXPLORE'
  }, [activeFlowMetadata, isExploreMode])

  const isSessionLoading = sessionStatus === 'idle' || sessionStatus === 'loading'
  const isSessionBlocked = sessionStatus === 'blocked'
  const isSessionError = sessionStatus === 'error'
  const showSessionState = isSessionLoading || isSessionBlocked || isSessionError

  const showConversationOptions = Boolean(
    sessionStatus === 'ready' && currentConversationId && headerTitle !== 'New Chat'
  )

  const messageInputHelperMessage = useMemo(() => {
    if (isCompareMode && isAwaitingUploadStep) {
      return 'Use the inline upload form in the conversation to add files for this Analyze step.'
    }
    if (activeMode !== 'DEFAULT') {
      return 'Use the options in the assistant message to continue the flow.'
    }
    return undefined
  }, [activeMode, isAwaitingUploadStep, isCompareMode])

  const headerStatusText = useMemo(() => {
    if (connectionState === 'connected') return 'Connected'
    if (connectionState === 'connecting') return 'Connecting'
    if (connectionState === 'reconnecting') return 'Reconnecting'
    return 'Disconnected'
  }, [connectionState])

  const ActiveEmptyComponent = emptyChatComponents[activeMode] ?? defaultEmptyComponent

  useEffect(() => {
    if (sessionStatus !== 'ready') {
      setShowReportsModal(false)
    }
  }, [sessionStatus])

  const handleConversationOptionSelect = (id: string) => {
    if (id === 'show-reports') {
      setShowReportsModal(true)
      return
    }
    onHeaderOptionSelect(id)
  }

  const getTabClass = (mode: ChatMode): string => {
    if (activeMode !== mode) return classes.tab
    if (mode === 'DEFAULT') return `${classes.tab} ${classes.activeDefaultTab}`
    if (mode === 'COMPARE') return `${classes.tab} ${classes.activeCompareTab}`
    if (mode === 'EXPLORE') return `${classes.tab} ${classes.activeExploreTab}`
    return `${classes.tab} ${classes.activeBudgetTab}`
  }

  const sessionStateTitle = isSessionLoading
    ? 'Loading MAX'
    : isSessionBlocked
    ? 'MAX unavailable for this tenant'
    : 'Unable to start MAX'

  const sessionStateBody = isSessionLoading
    ? 'Authenticating MAX and loading your workspace. Conversations will appear as soon as the session is ready.'
    : sessionError || 'Something went wrong while preparing MAX.'

  const transcriptContent = (isDragOver: boolean) => (
    <div className={classes.transcriptHost}>
      {transportError && sessionStatus === 'ready' ? <StatusErrorStrip message={transportError} /> : null}

      {showSessionState ? (
        <div className={classes.statusState}>
          <div className={classes.statusCard}>
            {isSessionLoading ? <CircularProgress size={28} className={classes.loadingSpinner} /> : null}
            <Typography component="h2" className={classes.statusTitle}>
              {sessionStateTitle}
            </Typography>
            <Typography component="p" className={classes.statusBody}>
              {sessionStateBody}
            </Typography>
          </div>
        </div>
      ) : hasMessages || isLoadingMessages ? (
        <MessagesContainer
          messages={currentMessages}
          isLoading={isLoadingMessages}
          isTyping={isTyping}
          activeAgentTrail={activeAgentTrail}
          agentTrailForMessage={agentTrailForMessage}
          ephemeralSurface={ephemeralSurface}
          isMessageFavorited={isMessageFavorited}
          onSelectFlowAction={onSelectFlowAction}
          onToolkitFavorite={onToolkitFavorite}
          onToolkitShare={onToolkitShare}
          onToolkitRetry={onToolkitRetry}
          onToolkitExportBudget={onToolkitExportBudget}
          onToolkitExportReport={onToolkitExportReport}
          onSubmitEphemeralSurface={onSubmitEphemeralSurface}
          onAttachFiles={onAttachFiles}
        />
      ) : isExploreMode ? (
        <ExploreEmptyState
          isLoading={exploreIsLoading}
          error={exploreError}
          categories={exploreCategories}
          cards={exploreCards}
          filteredCards={exploreFilteredCards}
          selectedCategory={exploreSelectedCategory}
          onSelectCategory={onExploreSelectCategory}
          onSelectCard={onExploreSelectCard}
          onReload={onExploreReload}
          onApplyCard={onExploreApplyCard}
          initialSelections={exploreInitialSelections}
        />
      ) : (
        <ActiveEmptyComponent
          onModeSelect={onModeSelect}
          onStartCompare={() => { void onStartCompare() }}
          onStartBudget={() => { void onStartBudget() }}
        />
      )}

      {isDragOver && (
        <div aria-hidden="true" className={classes.dragOverlay}>
          <div className={classes.dragOverlayInner}>
            <div className={classes.dragIconHost}>📎</div>
            <div className={classes.dragLabel}>Drop files to attach</div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <main
      className={classes.root}
      data-conversation-id={currentConversationId || undefined}
    >
      {/* Top tab bar */}
      <div className={classes.tabBar}>
        <div className={classes.tabGroup}>
          <button
            type="button"
            className={getTabClass('DEFAULT')}
            onClick={() => onModeSelect('DEFAULT')}
          >
            OpenChat
          </button>
          <button
            type="button"
            className={getTabClass('COMPARE')}
            disabled={isExploreFlowActive}
            onClick={() => onModeSelect('COMPARE')}
          >
            Analyze
          </button>
          {isExploreModeEnabled ? (
            <button
              type="button"
              className={getTabClass('EXPLORE')}
              onClick={() => onModeSelect('EXPLORE')}
            >
              Explore
              <span className={classes.sparkles}>
                <span className={classes.sparkleLarge}><SparkleIcon /></span>
                <span className={classes.sparkleSmall}><SparkleIcon /></span>
              </span>
            </button>
          ) : null}
          <button
            type="button"
            className={getTabClass('BUDGET')}
            disabled={isExploreFlowActive}
            onClick={() => onModeSelect('BUDGET')}
          >
            Budget
          </button>
        </div>
      </div>

      {/* Conversation header — only when there's an active conversation */}
      {currentConversationId ? (
        <ChatHeader
          title={headerTitle}
          isConnected={connectionState === 'connected'}
          statusText={headerStatusText}
          showConversationOptions={showConversationOptions}
          onOptionSelect={handleConversationOptionSelect}
        />
      ) : null}

      {/* Transcript wrapped in file dropzone */}
      {sessionStatus === 'ready' ? (
        <AttachmentDropzone
          accept={getAcceptString()}
          disabled={attachmentsDisabled || activeMode !== 'DEFAULT'}
          onFilesSelected={onAttachFiles}
        >
          {({ isDragOver }) => transcriptContent(isDragOver)}
        </AttachmentDropzone>
      ) : (
        transcriptContent(false)
      )}

      {/* Footer composer — only when session ready */}
      {sessionStatus === 'ready' ? (
        <div className={classes.footer}>
          <ChatComposer
            mode={activeMode}
            textDisabled={textDisabled}
            attachmentsDisabled={isCompareMode && isAwaitingUploadStep ? true : attachmentsDisabled}
            helperMessage={messageInputHelperMessage}
            preserveStagedAttachmentsWhenDisabled={isCompareMode && isAwaitingUploadStep}
            onSend={(content) => { void onSend(content) }}
            onAttachFiles={onAttachFiles}
          />
        </div>
      ) : null}

      {currentConversationId && sessionStatus === 'ready' ? (
        <ReportsListModal
          open={showReportsModal}
          conversationId={currentConversationId}
          reports={reports}
          hasCachedReports={reportsHasCached}
          isLoading={reportsLoading}
          error={reportsError}
          onClose={() => setShowReportsModal(false)}
          onFetchReports={onFetchReports}
          onDownloadReport={onDownloadReport}
        />
      ) : null}
    </main>
  )
}

export default ChatArea
